import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import dataService from '../services/dataService';

// Create the context
const DataContext = createContext();

// Custom hook to use the data context
export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

// Provider component
export const DataProvider = ({ children }) => {
  const [therapies, setTherapies] = useState([]);
  const [settings, setSettings] = useState({});
  const [services, setServices] = useState({});
  const [courses, setCourses] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState({
    therapies: true,
    settings: true,
    services: true,
    courses: true,
    faqs: true,
  });

  // Prevent duplicate data loading in StrictMode
  const dataLoadedRef = useRef(false);

  // Load all data once when the app starts
  useEffect(() => {
    if (dataLoadedRef.current) return;
    dataLoadedRef.current = true;

    const loadAllData = async () => {
      // Load therapies
      try {
        const therapiesData = await dataService.getTherapies();
        setTherapies(therapiesData);
        setLoading(prev => ({ ...prev, therapies: false }));
      } catch (error) {
        console.error('❌ Failed to load therapies globally:', error);
        setLoading(prev => ({ ...prev, therapies: false }));
      }

      // Load settings
      try {
        const settingsData = await dataService.getSettings();
        setSettings(settingsData);
        setLoading(prev => ({ ...prev, settings: false }));
      } catch (error) {
        console.error('❌ Failed to load settings globally:', error);
        setLoading(prev => ({ ...prev, settings: false }));
      }

      // Load services
      try {
        const servicesData = await dataService.getServices();
        setServices(servicesData);
        setLoading(prev => ({ ...prev, services: false }));
      } catch (error) {
        console.error('❌ Failed to load services globally:', error);
        setLoading(prev => ({ ...prev, services: false }));
      }

      // Load courses
      try {
        const coursesData = await dataService.getCourses();
        setCourses(coursesData);
        setLoading(prev => ({ ...prev, courses: false }));
      } catch (error) {
        console.error('❌ Failed to load courses globally:', error);
        setLoading(prev => ({ ...prev, courses: false }));
      }

      // Load FAQs
      try {
        const faqsData = await dataService.getFAQs();
        setFaqs(faqsData);
        setLoading(prev => ({ ...prev, faqs: false }));
      } catch (error) {
        console.error('❌ Failed to load FAQs globally:', error);
        setLoading(prev => ({ ...prev, faqs: false }));
      }
    };

    loadAllData();
  }, []);

  const value = {
    therapies,
    settings,
    services,
    courses,
    faqs,
    loading,
    // Provide refresh functions if needed
    refreshTherapies: async () => {
      setLoading(prev => ({ ...prev, therapies: true }));
      try {
        const data = await dataService.getTherapies();
        setTherapies(data);
      } catch (error) {
        console.error('Failed to refresh therapies:', error);
      } finally {
        setLoading(prev => ({ ...prev, therapies: false }));
      }
    },
    refreshSettings: async () => {
      setLoading(prev => ({ ...prev, settings: true }));
      try {
        const data = await dataService.getSettings();
        setSettings(data);
      } catch (error) {
        console.error('Failed to refresh settings:', error);
      } finally {
        setLoading(prev => ({ ...prev, settings: false }));
      }
    },
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};